import { PropsWithChildren, createContext, useContext, useState } from 'react';
import { UseMutationResult, useQueryClient } from 'react-query';
import { compact, flatten, isEmpty } from 'lodash';

import {
  GetSummaryReportsForSiteQueryParams,
  HiveGetSummaryReportsResponseDto,
  ReportIdInputDto,
  ReviewReportInputDto,
  UpdateEodReviewRequest,
} from '@pimm/services/lib/store-inspections';
import { useModalFocus } from '@app/hooks/modal-focus.hook';

type UpdateEodReviewFunc = UseMutationResult<any, Error, UpdateEodReviewRequest>;

export type ReviewVerifyReportsContextReturn = ReturnType<typeof useModalFocus<ReportIdInputDto[]>> & {
  confirmVerify: (_request: UpdateEodReviewFunc) => (_confirm?: boolean) => void;
  startPinSession: (ms?: number) => void;
  isPinSessionValid: () => boolean;
};

export type ReviewVerifyReportsProviderProps = PropsWithChildren & {
  siteId?: string;
  userId?: string;
  username?: string;
};

export const ReviewVerifyReportsContext = createContext<ReviewVerifyReportsContextReturn>(undefined!);

export const ReviewVerifyReportsProvider = ({ children, siteId, userId, username }: ReviewVerifyReportsProviderProps) => {
  const queryClient = useQueryClient();
  const modalFocus = useModalFocus<ReportIdInputDto[]>();

  const [pinValidUntil, setPinValidUntil] = useState(0);
  const startPinSession = (ms = 60_000) => setPinValidUntil(Date.now() + ms);
  const isPinSessionValid = () => Date.now() < pinValidUntil;

  const handleReviewVerify = (_callback: UpdateEodReviewFunc) => async (_confirm?: boolean) => {
    if (_confirm && siteId && userId && username && !isEmpty(modalFocus.payload)) {
      const reports = modalFocus.payload!;
      const payload: UpdateEodReviewRequest = {
        date: reports?.[0]?.storeReports?.[0]?.date,
        eodReview: {
          isReviewed: true,
          reviewTimestampUtc: new Date().toISOString(),
          reviewerUserId: userId,
          reviewerUserName: username,
        },
        storeReports: reports.flatMap(({ reportId, scheduleType, storeReports }): ReviewReportInputDto[] =>
          (storeReports ?? []).map(({ storeReportId }) => ({ reportId, scheduleType, storeReportId })),
        ),
        siteId: siteId,
      };

      const queryCache = queryClient.getQueryCache();
      const allQueries = queryCache.getAll();
      const storeReportIds = compact(reports.flatMap(_ => _.storeReports?.map(_ => _.storeReportId)));

      try {
        await _callback.mutateAsync(payload);

        for (const { queryKey } of allQueries) {
          const key0 = queryKey[0] as string;
          if (typeof key0 !== 'string' || !key0.startsWith('GetSummaryReportsForSite')) continue;
          const params = queryKey[1] as GetSummaryReportsForSiteQueryParams;
          const selectedDates = compact(reports.flatMap(({ storeReports }) => storeReports?.map(_ => _.date)));

          if (
            params?.startDate &&
            selectedDates.length > 0 &&
            // any selected date is within the range
            selectedDates.some(d => d! >= params.startDate! && d! <= params.endDate!)
          ) {
            // prettier-ignore
            const prevData = queryClient.getQueryData<HiveGetSummaryReportsResponseDto>(queryKey);
            if (prevData) {
              const updatedSummaryReports = prevData.summaryReportsByReportType?.map(({ summaryReports = [], ..._reportType }) => {
                return {
                  ..._reportType,
                  summaryReports: summaryReports.map(_report => {
                    if (!storeReportIds.includes(_report.storeReportId!)) return _report;
                    const daySummaries = _report.daySummaries?.map(_day =>
                      selectedDates.includes(_day.date as any) ? { ..._day, eodReview: payload.eodReview } : _day,
                    );
                    return { ..._report, daySummaries };
                  }),
                };
              });
              const nextData: HiveGetSummaryReportsResponseDto = {
                ...prevData,
                summaryReportsByReportType: updatedSummaryReports || [],
              };
              queryClient.setQueryData<HiveGetSummaryReportsResponseDto>(queryKey, nextData);
            }
          }
        }
      } catch (error) {}
    }

    modalFocus.setHide();
  };

  return (
    <ReviewVerifyReportsContext.Provider
      value={{
        ...modalFocus,
        confirmVerify: handleReviewVerify,
        startPinSession,
        isPinSessionValid,
      }}
    >
      {children}
    </ReviewVerifyReportsContext.Provider>
  );
};

export const ReviewVerifyReportsConsumer = ReviewVerifyReportsContext.Consumer;

export const useReviewVerifyReports = () => {
  // Get the context
  const context = useContext(ReviewVerifyReportsContext);
  // if `undefined`, throw an error
  if (context === undefined) {
    throw new Error('useReviewVerifyReports was used outside of its Provider');
  }
  return context;
};
